import React, { useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import { connect, ReactReduxContext } from 'react-redux';

import { bemGenerator } from '@prisjakt/ui-base';
import { AppContext } from '@prisjakt/ui-app-context';
import graphqlPropTypes from '@prisjakt/graphql-proptypes';
import Panel from '@prisjakt/ui-panel';
import PanelHeader from '@prisjakt/ui-panel-header';

import NewProductReleases from '../../components/new-product-releases';
import CampaignDeals from '../../components/campaign-deals';
import CampaignBanner from './components/campaign-banner';
import SotyBanner from './components/soty-banner';
import ForumTopicListing from './components/forum-topic-listing';
import searchActions from '../search/actions/search';
import PageContent from '../../components/page-content';
import FeaturedArticle from './components/featured-article';
import PromoBanner from '../../components/campaign-banners/promo-banner';
import RecentlyVisitedProducts from '../../components/recently-visited';
import { CategoryList } from '../../components/category-list';
import ProductRecommendations from './components/product-recommendations';
import AdContainer from '../../components/ads';
import {
  PersonalizedRecommendationsFragment,
  NewProductRecommendationsFragment,
  CampaignDealsRootFragment,
  ForumTopicsFragment,
} from '../../types';

import './index.scss';

const styles = bemGenerator('landing-page', [
  'campaign-filters',
  'tagline',
  'take-over-link',
  'content',
]);

const LandingPage = props => {
  const { t } = useContext(AppContext);
  const { store } = useContext(ReactReduxContext);

  useEffect(() => {
    store.dispatch({
      type: 'ADS_VIEW_UPDATE',
      view: 'landing-page:mounted',
    });
  }, [store]);

  const {
    bigCampaignBanner,
    activeCampaign,
    campaignBanners,
    data: {
      campaignDeals,
      forumTopics,
      personalizedRecommendations,
      fallbackRecommendations,
      shopOfTheYear,
      newProductRecommendations,
      featuredArticle,
      rootCategories,
    },
  } = props;

  const categories = (rootCategories || []).map(x => ({
    ...x,
    logo: { 192: x.logo },
  }));

  const activeCampaignLogo =
    activeCampaign &&
    activeCampaign.banners.find(
      banner => banner.type.toUpperCase() === 'BANNER_LOGO_BIG'
    );

  const activeCampaignAsCategory = activeCampaign && {
    id: 'activeCampaign',
    name: activeCampaign.name,
    pathName: activeCampaign.web_uri.responsive,
    logo: {
      192: activeCampaignLogo ? activeCampaignLogo.imageUrl : null,
    },
  };

  const beforeBanners =
    campaignBanners &&
    campaignBanners.active.filter(b => b && b.type === 'BEFORE_PROMO_BANNER');

  return (
    <div className={styles.block}>
      <PageContent withAds headerHeight={0} bottomPlacements={[]}>
        <div className={`column ${styles.content}`}>
          <Panel
            header={<PanelHeader title={t('landing', 'categories.title')} />}
            data-test-type="categories"
          >
            <CategoryList
              withAds
              categories={categories.concat(
                activeCampaignAsCategory ? [activeCampaignAsCategory] : []
              )}
            />
          </Panel>

          {bigCampaignBanner && (
            <CampaignBanner
              url={bigCampaignBanner.url}
              imageUrl={bigCampaignBanner.imageUrl}
              {...bigCampaignBanner.options}
            />
          )}

          {shopOfTheYear && shopOfTheYear.isVotingOpen && (
            <Panel>
              <SotyBanner year={shopOfTheYear.year} />
            </Panel>
          )}

          {beforeBanners && beforeBanners.length > 0 && (
            <PromoBanner inverted banners={beforeBanners} />
          )}

          {featuredArticle && (
            <FeaturedArticle
              {...featuredArticle}
              linkText={t('landing', 'featured-article.read-more')}
            />
          )}

          <RecentlyVisitedProducts />

          {fallbackRecommendations && fallbackRecommendations.products && (
            <ProductRecommendations {...fallbackRecommendations} />
          )}

          <AdContainer
            placement="dubbelpanorama2"
            min={0}
            max={860}
            spacing={{ bottom: '20px' }}
          />

          <CampaignDeals deals={campaignDeals} />

          {personalizedRecommendations &&
            personalizedRecommendations.products &&
            !personalizedRecommendations.products.fallbackToDefault && (
              <ProductRecommendations {...personalizedRecommendations} />
            )}

          {newProductRecommendations && (
            <NewProductReleases {...newProductRecommendations} />
          )}

          <AdContainer
            placement="dubbelpanorama3"
            min={0}
            max={860}
            spacing={{ bottom: '20px' }}
          />

          {forumTopics && <ForumTopicListing topics={forumTopics} />}
        </div>
      </PageContent>
    </div>
  );
};

const { bool, shape, string, arrayOf, number, oneOf, object } = PropTypes;

LandingPage.propTypes = {
  menu: bool,
  search: bool,
  activeCampaign: shape({
    id: number,
    name: string,
    type: string,
    tagline: string,
    description: shape({
      short: string,
      long: string,
      page: string,
    }),
    state: string,
    slug: string,
    web_uri: shape({
      mobile: string,
      desktop: string,
      responsive: string,
    }),
    banners: arrayOf(
      shape({
        type: string,
        url: string,
        imageUrl: string,
        options: shape({
          overlay: string,
          fallbackColor: string,
          tagline: shape({
            text: string,
            color: string,
            pos: oneOf(['top', 'bottom']),
            uppercase: bool,
          }),
          link: shape({
            url: string,
            text: string,
            color: string,
            border: string,
            background: string,
            pos: oneOf(['top', 'bottom']),
          }),
        }),
      })
    ),
  }),
  data: shape({
    campaignDeals: graphqlPropTypes(CampaignDealsRootFragment),
    forumTopics: graphqlPropTypes(ForumTopicsFragment),
    personalizedRecommendations: graphqlPropTypes(
      PersonalizedRecommendationsFragment
    ),
    fallbackRecommendations: graphqlPropTypes(
      PersonalizedRecommendationsFragment
    ),
    newProductRecommendations: graphqlPropTypes(
      NewProductRecommendationsFragment
    ),
  }),
  bigCampaignBanner: shape({
    url: string,
    imageUrl: string,
    options: shape({
      badgeUrl: string,
      badgeAltText: string,
      linkText: string,
      description: string,
    }),
  }),
  campaignBanners: shape({
    active: arrayOf(object),
  }),
};

LandingPage.defaultProps = {
  data: {
    forumTopics: undefined,
    personalizedRecommendations: undefined,
    fallbackRecommendations: undefined,
    newProductRecommendations: undefined,
    campaignDeals: undefined,
  },
  bigCampaignBanner: undefined,
  menu: true,
  search: true,
  activeCampaign: undefined,
  campaignBanners: undefined,
};

export default connect(
  state => ({
    activeCampaign:
      state.globals.campaigns &&
      state.globals.campaigns.active.find(
        c => c.type === 'OFFER' || c.type === 'SEARCH_BANNER'
      ),
    bigCampaignBanner:
      state.globals.campaignBanners &&
      state.globals.campaignBanners.active.find(
        banner => banner.type === 'LANDING_PAGE_BIG'
      ),
    campaignBanners: state.globals.campaignBanners,
  }),
  searchActions
)(LandingPage);
